import { useId } from "react";
import { addWeeks, format, isBefore, isSameWeek, startOfDay } from "date-fns";
import { CalendarDays, ChevronsRight, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { timezones } from "../../lib/schedule-time/schedule-dates";
import { timezoneLabel } from "../../lib/schedule-time/timezone-label";
import { useScheduleStore } from "../../stores/schedule-store";
import { dispatchScheduleEvent } from "../../lib/schedule-history/dispatch";
import { WeekPicker } from "./WeekPicker";
import { Tooltip } from "radix-ui";

export function SchedulePeriod() {
  const week = useScheduleStore(state => state.week);
  const timezone = useScheduleStore(state => state.timezone);
  const selectedDate = useScheduleStore(state => state.selectedDate);

  return <SchedulePeriodForm week={week} timezone={timezone} selectedDate={selectedDate}
    onDateChange={date => dispatchScheduleEvent({ type: "date/set", date: format(date, "yyyy-MM-dd") })}
    onTimezoneChange={timezone => dispatchScheduleEvent({ type: "timezone/set", timezone })} />;
}

type SchedulePeriodFormProps = {
  week: Date[]; timezone: string; selectedDate: Date;
  onDateChange: (date: Date) => void; onTimezoneChange: (timezone: string) => void;
  warnPastWeek?: boolean;
};

export function SchedulePeriodForm({ week, timezone, selectedDate, onDateChange, onTimezoneChange, warnPastWeek = true }: SchedulePeriodFormProps) {
  const { t } = useTranslation();
  const id = useId();
  const weekId = `${id}-week`;
  const timezoneId = `${id}-timezone`;

  // Read at render so a schedule restored from localStorage is checked against today.
  const today = new Date();
  const weekStart = week[0]!;
  const weekHasStarted = warnPastWeek && isBefore(weekStart, startOfDay(today));
  const nextWeekDate = addWeeks(today, 1);
  const isNextWeekSelected = isSameWeek(selectedDate, nextWeekDate, { weekStartsOn: 1 });
  const selectNextWeek = () => onDateChange(nextWeekDate);

  return (
    <section>
      <div className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] items-start gap-3">
        <div className="min-w-0 space-y-1.5">
          <label htmlFor={weekId} className="text-[11px] font-medium text-neutral-600">{t("period.week")}</label>
          <div className="flex">
            <WeekPicker id={weekId} selectedDate={selectedDate} week={week} onSelect={onDateChange} warning={weekHasStarted} className="rounded-r-none" />
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <Button type="button" variant="outline" size="icon" aria-label={t("period.nextWeek")} className="-ml-px h-9 shrink-0 rounded-l-none border-neutral-300"
                    disabled={isNextWeekSelected} onClick={selectNextWeek}>
                    <span className="relative flex size-4 items-center justify-center">
                      <CalendarDays className="size-4" />
                      <ChevronsRight className="absolute -right-1 -top-0.5 size-2.5 bg-white" />
                    </span>
                  </Button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content side="top" className="z-50 rounded bg-neutral-900 px-2 py-1 text-xs text-white shadow">
                    {t("period.nextWeek")}
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          </div>
          <p className="flex min-h-4 items-center gap-1.5 text-[11px] font-medium text-amber-700">
            {weekHasStarted && <><TriangleAlert className="size-3.5 shrink-0" />{t("period.pastWarning")}</>}
          </p>
        </div>
        <div className="min-w-0 space-y-1.5">
          <label htmlFor={timezoneId} className="text-[11px] font-medium text-neutral-600">{t("period.timezone")}</label>
          <select id={timezoneId} value={timezone} onChange={event => onTimezoneChange(event.target.value)} className="h-9 w-full min-w-0 rounded border border-neutral-300 bg-white px-2 font-mono text-xs focus-visible:outline-2 focus-visible:outline-neutral-900">
            {timezones.map(zone => <option key={zone} value={zone}>{timezoneLabel(zone, week[0]!, t)}</option>)}
          </select>
        </div>
      </div>
    </section>
  );
}
